<template>
  <BaseModal cardWidth="480px" noPadding noToolbar noExit @close="handleClose">
    <template #default>
      <!-- Modal Header -->
      <div class="relative w-full flex items-center p-5 border-b border-border-normal">
        <BaseText type="label" size="sm" class="text-text-muted">
          Start Spying on {{ brand.name }}?
        </BaseText>
        <!-- Close button -->
        <button class="absolute top-3 right-3 p-1" @click="handleClose">
          <TagRemoveIcon stroke="#5E6678" :width="16" :height="16" />
        </button>
      </div>
      <!-- Brand Information -->
      <div class="flex items-center gap-3 p-5 border-b border-border-normal">
        <BrandAvatar :avatar-src="brand.avatar_url" :brand-name="brand.name" :width="40" :height="40" class="rounded-lg" />
        <div class="flex flex-col">
          <BaseText type="label" size="sm" class="text-text-muted">
            {{  brand.name }}
          </BaseText>
          <BaseText v-if="brand?.analytics?.active_count" type="body" size="sm" class="text-text-normal">
            {{ brand.analytics.active_count.toLocaleString() }} active ads
          </BaseText>
        </div>
      </div>
      <!-- Folder Select, Credits, & Submit -->
      <div class="w-full flex flex-col gap-4 p-5">
        <div class="relative w-full z-10">
          <BaseSingleDropdown
            use-parent-width
            enable-search
            search-placeholder="Search Folders"
            label="Location"
            show-label-space
            :options="folderOptions"
            :selected-obj="selectedFolder"
            @change="selectedFolder = $event"
          >
            <template #icon>
              <FolderIcon class="text-icon-normal" />
            </template>
          </BaseSingleDropdown>
        </div>
        <div class="w-full flex items-center justify-between">
          <!-- Spyder Credits -->
          <BaseTooltipV2 position="left" :offset="16">
            <div ref="creditsContainer" class="flex items-center gap-2 pl-0.5 py-0.5 pr-3 rounded-full 
            text-text-muted transition-colors duration-100 hover:bg-neutral-25 cursor-default">
              <img src="../../assets/icons/spyder-token.png" class="w-6 h-6">
              <BaseText type="label" size="sm" class="">
                {{ creditsUsed }}/{{ getSpyderLimit ?? 2 }} credits
              </BaseText>
            </div>
            <template #tooltip>
              <div class="relative rounded-xl w-max flex flex-col gap-1 items-center p-1.5 bg-neutral-900 shadow-md cursor-default">
                <img src="../../assets/icons/spyder-token.png" class="w-7 h-7 mb-0.5 mt-2.5">
                <div class="w-full flex flex-col items-center gap-1 px-3 pb-3 pt-1">
                  <BaseText type="label" size="md" class="text-white">
                    Spyder Credits
                  </BaseText>
                  <BaseText v-if="getSpyderLimit - creditsUsed > 0" type="body" size="sm" class="text-text-disabled">
                    You have {{ getSpyderLimit - creditsUsed }} credits available.
                  </BaseText>
                  <BaseText v-else type="body" size="sm" class="text-text-disabled">
                    You have no remaining credits.
                  </BaseText>
                </div>
                <div class="arrow-right absolute left-full top-1/2 transform -translate-y-1/2" />
              </div>
            </template>
          </BaseTooltipV2>
          <!-- Submit Button -->
          <!-- Credits can't be counted until the subscriptions load (e.g. just after a page load) -->
          <BasePrimaryButton
            text="Start Tracking"
            :disabled="submitDisabled"
            :loading="submitLoading || getSpyderSubscriptionsLoading"
            allow-disabled-click
            @click="trackBrand"
          />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'
import { normalizeBrandsForSubscriptionsList } from '@/utils/spyder/brandUtils'

import BrandAvatar from '../brands/BrandAvatar.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import FolderIcon from '../globals/Icons/SpyderV2Icons/FolderIcon.vue'
import SpyderIcon from '../globals/Icons/SidebarIcons/SpyderIcon.vue'

export default {
  name: 'TrackBrandModal',
  components: {
    BrandAvatar,
    TagRemoveIcon,
    FolderIcon,
    SpyderIcon
  },
  props: {
    brand: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      selectedFolder: { id: null, name: 'Default Folder' },
      submitLoading: false,
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getSpyderLimit']),
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getPendingSpyderBrands', 'getSpyderSubscriptionsLoading']),
    folderOptions () {
      const folders = this.getFolders
        .filter(folder => folder.id !== this.selectedFolder.id)
        .sort((a, b) => a.name.localeCompare(b.name))
      if (this.selectedFolder.id) {
        folders.unshift({ id: null, name: 'Default Folder' })
      }
      return folders // sort alphabetically
    },
    spyderSubscriptions () {
      return normalizeBrandsForSubscriptionsList(this.getSpyderSubscriptions, this.getPendingSpyderBrands)
    },
    creditsUsed () {
      return this.spyderSubscriptions?.length ?? 0
    },
    submitDisabled () {
      return this.creditsUsed >= this.getSpyderLimit
    }
  },
  methods: {
    async trackBrand () {
      if (this.submitDisabled) {
        this.animateCreditShake()
        return
      }
      this.submitLoading = true
      try {
        await ForeplayAPI.Spyder.subscribeToBrand(this.brand.id, this.selectedFolder.id)
        await new Promise(resolve => setTimeout(resolve, 2000)) // Wait 2 secs to account for db update delays
        this.$emit('subscribed')
        this.$emit('close')
      } catch (error) {
        console.error('Error subscribing to brand', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to subscribe to brand. See console for details.'
        })
      } finally {
        this.submitLoading = false
      }
    },
    animateCreditShake () {
      const creditsContainer = this.$refs.creditsContainer
      if (!creditsContainer) return
      creditsContainer.classList.add('animate-shake')
      setTimeout(() => { creditsContainer.classList.remove('animate-shake') }, 300)
    },
    handleClose () {
      if (this.submitLoading) return
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.arrow-right {
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid #06070F;
}

.animate-shake {
  animation: shake 0.3s cubic-bezier(.36,.07,.19,.97) both;
}
@keyframes shake {
  0% { 
    transform: translateX(0); 
    background-color: transparent; 
    color: default;
  }
 25% { transform: translateX(3px) }
 50% { 
  transform: translateX(-3px); 
  background-color: #FDF2F1; 
  color: #AB2D27;
}
 75% { transform: translateX(2px) }
 100% { 
  transform: translateX(0); 
  background-color: transparent; 
  color: default;
}
}
</style>